import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Upload, Check, Loader2, MessageCircle, FileText } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { fmt, PAYMENT_METHODS } from "@/lib/site";

// Seguimiento público del pedido: el cliente entra con su número de pedido,
// ve en qué va, lee cómo pagar y SUBE el comprobante. Sin login: pedirle una
// cuenta a un carpintero para ver su propio pedido es fricción que no paga.
const pasos = (retira) => [
    { id: "pendiente", t: "Pedido recibido" },
    { id: "confirmado", t: "Confirmado" },
    { id: "por_verificar", t: "Pago por verificar" },
    { id: "pagado", t: "Pago verificado" },
    { id: "despachado", t: retira ? "Listo para retirar" : "Despachado" },
    { id: "entregado", t: retira ? "Retirado" : "Entregado" },
];

export default function SeguimientoPedido() {
    const { orderNumber } = useParams();
    const [data, setData] = useState(null);
    const [error, setError] = useState("");
    const [subiendo, setSubiendo] = useState(false);
    const fileRef = useRef(null);

    const cargar = useCallback(() => {
        api.get(`/orders/track/${orderNumber}`)
            .then((r) => { setData(r.data); setError(""); })
            .catch(() => setError("No encontramos ese pedido. Revisa el número o escríbenos por WhatsApp."));
    }, [orderNumber]);

    useEffect(() => {
        document.title = `Pedido ${orderNumber} · Maderas La Romanilla`;
        cargar();
    }, [orderNumber, cargar]);

    const subir = async (e) => {
        const files = [...(e.target.files || [])];
        if (!files.length) return;
        const fd = new FormData();
        files.forEach((f) => fd.append("files", f));
        setSubiendo(true);
        try {
            await api.post(`/orders/track/${orderNumber}/comprobante`, fd,
                { headers: { "Content-Type": "multipart/form-data" } });
            toast.success("Comprobante recibido", { description: "Lo verificamos y te confirmamos por WhatsApp." });
            cargar();
        } catch (err) {
            toast.error("No se pudo subir", { description: err?.response?.data?.detail || "Intenta de nuevo" });
        } finally {
            setSubiendo(false);
            if (fileRef.current) fileRef.current.value = "";
        }
    };

    if (error) {
        return (
            <div className="min-h-screen bg-crema flex items-center justify-center px-5 text-center">
                <div>
                    <h1 className="font-display font-bold text-2xl text-ink">{error}</h1>
                    <Link to="/catalogo" className="inline-block mt-6 bg-verde text-white rounded-full px-7 py-3 text-sm font-semibold">Ver el catálogo</Link>
                </div>
            </div>
        );
    }
    if (!data) return <div className="min-h-screen bg-crema flex items-center justify-center text-mut">Cargando tu pedido…</div>;

    const retira = data.entrega === "retiro";
    const PASOS = pasos(retira);
    const idx = PASOS.findIndex((p) => p.id === data.estado);
    const cancelado = data.estado === "cancelado";
    const wa = data.whatsapp || "584220800620";
    const metodo = PAYMENT_METHODS.find((m) => m.id === data.metodo_pago);

    return (
        <div className="min-h-screen bg-crema pb-16">
            <header className="hero-gradient text-white px-5 py-8 text-center">
                <Link to="/"><img src="/logo-romanilla-light.png" alt="Maderas La Romanilla" className="h-14 mx-auto" /></Link>
                <p className="text-[11px] uppercase tracking-[0.25em] text-madera font-semibold mt-4">Tu pedido</p>
                <h1 className="font-display font-extrabold text-3xl mt-1">{data.order_number}</h1>
                <p className="font-display text-2xl text-madera mt-2">{fmt(data.total)}</p>
                {data.iva ? <p className="text-xs opacity-75">{fmt(data.subtotal)} + IVA {fmt(data.iva)}</p> : null}
            </header>

            <main className="max-w-2xl mx-auto px-4 -mt-5">
                {/* En qué va */}
                <section className="bg-hueso border border-linea rounded-2xl p-5 shadow-soft">
                    <h2 className="text-xs uppercase tracking-[0.2em] font-semibold text-madera mb-4">En qué va</h2>
                    {retira && !cancelado && (
                        <p className="text-xs text-madera-dark font-semibold mb-3">🏪 Retiras en nuestra tienda de El Llanito, Caracas.</p>
                    )}
                    {cancelado ? (
                        <p className="text-sm text-red-700 font-semibold">Este pedido fue cancelado. Escríbenos si fue un error.</p>
                    ) : (
                        <ol className="space-y-3">
                            {PASOS.map((p, i) => {
                                const hecho = i <= idx;
                                return (
                                    <li key={p.id} className="flex items-center gap-3">
                                        <span className={`h-6 w-6 shrink-0 rounded-full flex items-center justify-center text-[11px] font-bold ${hecho ? "bg-verde text-white" : "bg-white border border-linea text-mut"}`}>
                                            {hecho ? <Check size={13} /> : i + 1}
                                        </span>
                                        <span className={`text-sm ${i === idx ? "font-bold text-ink" : hecho ? "text-mut" : "text-mut/60"}`}>{p.t}</span>
                                    </li>
                                );
                            })}
                        </ol>
                    )}
                </section>

                {/* Qué pediste */}
                <section className="bg-hueso border border-linea rounded-2xl p-5 shadow-soft mt-4">
                    <h2 className="text-xs uppercase tracking-[0.2em] font-semibold text-madera mb-3">Qué pediste</h2>
                    <div className="divide-y divide-linea">
                        {data.items?.map((i, n) => (
                            <div key={n} className="flex items-center gap-3 py-2.5">
                                <span className="text-sm flex-1">{i.cantidad}× {i.nombre}</span>
                                <span className="text-sm font-semibold">{fmt(i.subtotal)}</span>
                            </div>
                        ))}
                    </div>
                    <div className="flex justify-between items-end border-t border-linea pt-3 mt-1">
                        <span className="text-xs uppercase tracking-widest text-mut">Total</span>
                        <span className="text-right">{data.iva ? <span className="block text-xs text-mut">{fmt(data.subtotal)} + IVA {fmt(data.iva)}</span> : null}<span className="font-display font-extrabold text-2xl text-verde">{fmt(data.total)}</span></span>
                    </div>
                </section>

                {/* Cómo pagar + subir comprobante */}
                {!cancelado && data.estado !== "entregado" && (
                    <section className="bg-hueso border border-linea rounded-2xl p-5 shadow-soft mt-4">
                        <h2 className="text-xs uppercase tracking-[0.2em] font-semibold text-madera mb-3">
                            Cómo pagar{metodo ? ` · ${metodo.label}` : ""}
                        </h2>
                        {data.instrucciones_pago ? (
                            <p className="text-sm text-ink whitespace-pre-line bg-white border border-linea rounded-xl p-4">{data.instrucciones_pago}</p>
                        ) : (
                            <p className="text-sm text-mut">Te enviamos los datos de pago por WhatsApp.</p>
                        )}

                        <div className="mt-4">
                            <p className="text-sm font-semibold text-ink">Ya pagué</p>
                            <p className="text-xs text-mut mt-0.5">Sube la captura o el comprobante y lo verificamos.</p>
                            <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp,application/pdf"
                                multiple onChange={subir} className="hidden" data-testid="track-file" />
                            <button onClick={() => fileRef.current?.click()} disabled={subiendo}
                                data-testid="track-subir"
                                className="mt-3 w-full flex items-center justify-center gap-2 bg-naranja text-white rounded-full py-3.5 text-sm font-bold disabled:opacity-60">
                                {subiendo ? <><Loader2 size={16} className="animate-spin" /> Subiendo…</> : <><Upload size={16} /> Subir mi comprobante</>}
                            </button>
                            {data.comprobantes > 0 && (
                                <p className="text-xs text-verde font-semibold mt-2 flex items-center gap-1.5">
                                    <FileText size={13} /> {data.comprobantes} comprobante{data.comprobantes > 1 ? "s" : ""} recibido{data.comprobantes > 1 ? "s" : ""}
                                </p>
                            )}
                        </div>
                    </section>
                )}

                <a href={`https://wa.me/${wa}?text=${encodeURIComponent(`Hola, es sobre mi pedido ${data.order_number}`)}`}
                    target="_blank" rel="noreferrer" data-testid="track-wa"
                    className="mt-4 w-full flex items-center justify-center gap-2 bg-verde text-white rounded-full py-3.5 text-sm font-bold">
                    <MessageCircle size={16} /> Escribirnos por WhatsApp
                </a>
            </main>
        </div>
    );
}
